import { useEffect, useMemo, useRef } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { calculateCompassAngle } from '../../hooks/useModelFrames.js'

import styles from './DirectionTicker.module.scss'

const DIRECTION_TICK_PX = 64          // fallback before DOM measurement
const DIRECTIONS_COUNT = 4            // N E S W
const TRACK_REPEAT_GROUPS = 5         // must be odd
const MIDDLE_GROUP_IDX = Math.floor(TRACK_REPEAT_GROUPS / 2)  // = 2
// The N label in the middle group — this is what sits under the needle at heading=0.
const ANCHOR_ITEM_IDX = MIDDLE_GROUP_IDX * DIRECTIONS_COUNT   // = 8

function normalizeAngle(deg) {
  return ((deg % 360) + 360) % 360
}

function viewHeadingAngleFromFrame(frame, framesCount, azimuth) {
  return normalizeAngle(-calculateCompassAngle(frame, framesCount, azimuth))
}

function directionKeyFromAngle(deg) {
  const normalized = normalizeAngle(deg)
  if (normalized >= 315 || normalized < 45) return 'N'
  if (normalized < 135) return 'E'
  if (normalized < 225) return 'S'
  return 'W'
}

function createDirectionLabels(t) {
  return {
    N: t('directions.compass_direction_n'),
    E: t('directions.compass_direction_e'),
    S: t('directions.compass_direction_s'),
    W: t('directions.compass_direction_w'),
  }
}

export function DirectionTicker({
  currentFrameRef,
  framesCount,
  azimuth = 0,
}) {
  const { t } = useI18n()

  const scaleRef = useRef(null)
  const trackRef = useRef(null)

  const directionLabels = useMemo(() => createDirectionLabels(t), [t])
  const directionTrackLabels = useMemo(() => {
    const base = Object.values(directionLabels)
    return Array.from({ length: TRACK_REPEAT_GROUPS }, () => base).flat()
  }, [directionLabels])

  useEffect(() => {
    if (!currentFrameRef) return undefined

    let rafId = null
    let lastFrame = null

    const update = () => {
      const frame = Number(currentFrameRef.current ?? 0)
      if (frame !== lastFrame) {
        const headingAngle = viewHeadingAngleFromFrame(frame, framesCount, azimuth)

        // Use real rendered item width so px maths stays in sync with CSS.
        const stepPx = trackRef.current?.firstElementChild?.getBoundingClientRect().width
          || DIRECTION_TICK_PX
        const cyclePx = stepPx * DIRECTIONS_COUNT

        // shiftPx: how far to scroll for the current heading.
        const shiftPx = headingAngle / 360 * cyclePx

        // baseOffsetPx: distance from track left-edge to centre of N in middle group.
        // We want that N under the needle when shiftPx = 0.
        const baseOffsetPx = (ANCHOR_ITEM_IDX + 0.5) * stepPx

        if (scaleRef.current) {
          // Ticks scroll leftward with labels (same perceived direction).
          scaleRef.current.style.backgroundPositionX = `${-shiftPx}px`
        }

        if (trackRef.current) {
          // Track left-edge is at left:50% of scaleWrap; shift so target label is at centre.
          const translateX = -(baseOffsetPx + shiftPx)
          trackRef.current.style.transform = `translate3d(${translateX}px, -50%, 0)`
        }

        lastFrame = frame
      }
      rafId = requestAnimationFrame(update)
    }

    update()

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [azimuth, currentFrameRef, framesCount])

  const currentDirectionLabel = directionLabels[
    directionKeyFromAngle(
      viewHeadingAngleFromFrame(currentFrameRef?.current ?? 0, framesCount, azimuth),
    )
  ] ?? directionLabels.N

  return (
    <div
      className={styles.directionTicker}
      aria-live="polite"
      aria-atomic="true"
      aria-label={currentDirectionLabel}
    >
      <div className={styles.scaleWrap}>
        <div ref={scaleRef} className={styles.scale} />
        <div ref={trackRef} className={styles.track}>
          {directionTrackLabels.map((label, idx) => (
            <span key={`${label}-${idx}`} className={styles.trackItem}>{label}</span>
          ))}
        </div>
        <span className={styles.needle} />
      </div>
    </div>
  )
}
